import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../button/Button";
import Input from "../../input/Input";
import CodeInput, { CodeInputHandle } from "../../CodeInput/CodeInput";
import styles from "./CodeEmailChangeModal.module.scss";
import close from "../../../assets/icons/close.svg";
import code from "../../../assets/pin-code.png";
import { useAppSelector } from "../../../store/hooks";
import { useEmailChangeConfirmMutation, useEmailChangeStartMutation } from "../../../store/services/auth-api";
import { getApiErrorMessage } from "../../../utils/codeLogin";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const emptyCode = () => Array<string>(CODE_LENGTH).fill("");

interface CodeEmailChangeModalProps {
  setIsVisible: (arg: boolean) => void;
  onEmailUpdate: (email: string) => void;
}

// Email change for users signed in with an email code: new email, then one code from the current
// address and one code from the new address
const CodeEmailChangeModal: React.FC<CodeEmailChangeModalProps> = ({ setIsVisible, onEmailUpdate }) => {
  const { t } = useTranslation();
  const currentEmail = useAppSelector((state) => state.auth.user?.email) ?? "";
  const [emailChangeStart] = useEmailChangeStartMutation();
  const [emailChangeConfirm] = useEmailChangeConfirmMutation();
  const codeOldRef = useRef<CodeInputHandle>(null);
  const codeNewRef = useRef<CodeInputHandle>(null);
  const isSubmittingRef = useRef(false);

  const [step, setStep] = useState<"email" | "codes">("email");
  const [emailFocused, setEmailFocused] = useState(true);
  const [emailValue, setEmailValue] = useState("");
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");
  const [codeOld, setCodeOld] = useState<string[]>(emptyCode);
  const [codeNew, setCodeNew] = useState<string[]>(emptyCode);
  const [isLoader, setIsLoader] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCoolDown, setResendCoolDown] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const newEmail = emailValue.trim();
  const emailInvalid = newEmail !== "" && !EMAIL_PATTERN.test(newEmail);
  const emailMismatch = confirmValue !== "" && confirmValue.trim() !== newEmail;
  const canStart = newEmail !== "" && confirmValue !== "" && !emailInvalid && !emailMismatch;

  useEffect(() => {
    if (resendCoolDown <= 0) return;
    const timerId = setTimeout(() => setResendCoolDown((prev) => prev - 1), 1000);
    return () => clearTimeout(timerId);
  }, [resendCoolDown]);

  // Sends one code to the current email and one to the new email (also used by "Resend codes")
  const startEmailChange = async (isResend: boolean) => {
    if (!canStart || isLoader || isResending) return;

    setErrorMsg("");
    setInfoMsg("");
    if (isResend) setIsResending(true);
    else setIsLoader(true);
    try {
      await emailChangeStart({ newEmail }).unwrap();
      setCodeOld(emptyCode());
      setCodeNew(emptyCode());
      setResendCoolDown(RESEND_COOLDOWN_SECONDS);
      if (isResend) {
        setInfoMsg(t("We sent you new codes."));
        codeOldRef.current?.focus();
      }
      setStep("codes");
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
    } finally {
      setIsLoader(false);
      setIsResending(false);
    }
  };

  const confirmEmailChange = async (oldValue: string, newValue: string) => {
    if (isSubmittingRef.current) return;
    if (oldValue.length !== CODE_LENGTH || newValue.length !== CODE_LENGTH) {
      setInfoMsg("");
      setErrorMsg(t("Please enter both 6-digit codes."));
      return;
    }

    isSubmittingRef.current = true;
    setErrorMsg("");
    setInfoMsg("");
    setIsLoader(true);
    try {
      const payload = await emailChangeConfirm({ codeOld: oldValue, codeNew: newValue }).unwrap();
      const updatedEmail = payload?.email || newEmail;
      // Same success handling as today's email change
      sessionStorage.setItem("email", updatedEmail);
      onEmailUpdate(updatedEmail);
      setIsVisible(false);
    } catch (error) {
      isSubmittingRef.current = false;
      setIsLoader(false);
      setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
    }
  };

  const closeButton = (
    <button type="button" className={styles.root_close} onClick={() => setIsVisible(false)} aria-label={t("Close")}>
      <img src={close} alt="" />
    </button>
  );

  if (step === "codes") {
    return (
      <form
        className={styles.root}
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
            e.preventDefault();
            confirmEmailChange(codeOld.join(""), codeNew.join(""));
          }
        }}
        noValidate
      >
        {closeButton}
        <img src={code} alt="" className={styles.root_image} />
        <h2 className={styles.root_title}>{t("Confirm your email change")}</h2>
        <p className={styles.root_subtitle}>
          {t("We sent one code to your current email and another code to your new email. Check your spam folder too.")}
        </p>

        <div className={styles.root_codes}>
          <div className={styles.root_codes_item}>
            <p className={styles.root_codes_label}>
              {t("Code sent to your current email ({{email}})", { email: currentEmail })}
            </p>
            <CodeInput
              ref={codeOldRef}
              values={codeOld}
              onChange={setCodeOld}
              onComplete={(value) => {
                if (codeNew.every((digit) => digit !== "")) confirmEmailChange(value, codeNew.join(""));
                else codeNewRef.current?.focus();
              }}
              ariaLabel={t("Code sent to your current email ({{email}})", { email: currentEmail })}
              autoFocus
              readOnly={isLoader}
              error={errorMsg !== ""}
            />
          </div>
          <div className={styles.root_codes_item}>
            <p className={styles.root_codes_label}>
              {t("Code sent to your new email ({{email}})", { email: newEmail })}
            </p>
            <CodeInput
              ref={codeNewRef}
              values={codeNew}
              onChange={setCodeNew}
              onComplete={(value) => {
                if (codeOld.every((digit) => digit !== "")) confirmEmailChange(codeOld.join(""), value);
                else codeOldRef.current?.focus();
              }}
              ariaLabel={t("Code sent to your new email ({{email}})", { email: newEmail })}
              readOnly={isLoader}
              error={errorMsg !== ""}
            />
          </div>
        </div>

        {errorMsg && (
          <p className={styles.root_error} role="alert">
            {errorMsg}
          </p>
        )}
        {infoMsg && (
          <p className={styles.root_info} role="status">
            {infoMsg}
          </p>
        )}

        <Button
          onClick={() => confirmEmailChange(codeOld.join(""), codeNew.join(""))}
          text={t("Confirm")}
          classNames={styles.root_button}
          isLoader={isLoader}
        />

        <div className={styles.root_links}>
          <button
            type="button"
            className={styles.root_link}
            onClick={() => startEmailChange(true)}
            disabled={resendCoolDown > 0 || isResending || isLoader}
          >
            {resendCoolDown > 0 ? t("Resend codes in {{seconds}}s", { seconds: resendCoolDown }) : t("Resend codes")}
          </button>
          <button
            type="button"
            className={styles.root_link}
            onClick={() => {
              setErrorMsg("");
              setInfoMsg("");
              setStep("email");
            }}
            disabled={isLoader}
          >
            {t("Use a different email")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className={styles.root}
      onSubmit={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
          e.preventDefault();
          startEmailChange(false);
        }
      }}
      noValidate
    >
      {closeButton}
      <h2 className={styles.root_title}>{t("Update Your Email Address")}</h2>
      <p className={styles.root_subtitle}>{t("Keep Your Fortect Account Information Up-to-Date")}</p>

      <div className={styles.root_inputs}>
        <Input
          type="email"
          value={emailValue}
          onChange={(e) => {
            setEmailValue(e.target.value);
            setErrorMsg("");
          }}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          focused={emailFocused}
          placeholder={t("Enter new Email")}
          error={emailInvalid && !emailFocused}
        />
        <Input
          type="email"
          value={confirmValue}
          onChange={(e) => {
            setConfirmValue(e.target.value);
            setErrorMsg("");
          }}
          onFocus={() => setConfirmFocused(true)}
          onBlur={() => setConfirmFocused(false)}
          focused={confirmFocused}
          placeholder={t("Confirm new Email")}
          error={emailMismatch}
        />
      </div>

      <div className={styles.root_messages}>
        {errorMsg ? (
          <p className={styles.root_error} role="alert">
            {errorMsg}
          </p>
        ) : emailInvalid && !emailFocused ? (
          <p className={styles.root_error}>{t("Email must be legal address")}</p>
        ) : emailMismatch ? (
          <p className={styles.root_error}>{t("Email must match.")}</p>
        ) : (
          confirmValue !== "" && <p className={styles.root_info}>{t("Email match.")}</p>
        )}
      </div>

      <p className={styles.root_note}>
        {t("We will send a code to your current email and another code to your new email.")}
      </p>

      <Button
        onClick={() => startEmailChange(false)}
        text={t("Change Email")}
        type={canStart ? "primary" : "disabled"}
        classNames={styles.root_button}
        isLoader={isLoader}
      />
    </form>
  );
};

export default CodeEmailChangeModal;
